import { useSyncExternalStore, useMemo, useCallback, startTransition } from "react";
import { DateTime } from "luxon";
import {
  SubmissionStatus,
  RegulatoryStatusOption,
  WasteSubmissionsSummaryFilter,
} from "generated-graphql/graphql";
import { metricsLogger } from "utils/metricsLogger";

// US states for validation
const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "DC", "PR", "VI", "GU", "AS", "MP"
];

const ALL_STATUSES = Object.values(SubmissionStatus);
const ALL_GENERATOR_STATUSES = Object.values(RegulatoryStatusOption);

/**
 * Filter keys that affect chart queries (NOT pagination/sorting).
 * When these change, charts should refetch.
 * When pagination changes, charts should NOT refetch.
 */
export interface FilterKeys {
  dueDateStart: string | null;
  dueDateEnd: string | null;
  states: string[] | null;
  statuses: SubmissionStatus[] | null;
  generatorStatuses: RegulatoryStatusOption[] | null;
  facilityTagNames: string[] | null;
  readyForActionEmail: string | null;
}

interface OmnisearchState {
  omnisearch: string;
  filterKeys: FilterKeys;
}

/**
 * Initial empty filter keys.
 */
const INITIAL_FILTER_KEYS: FilterKeys = {
  dueDateStart: null,
  dueDateEnd: null,
  states: null,
  statuses: null,
  generatorStatuses: null,
  facilityTagNames: null,
  readyForActionEmail: null,
};

/**
 * Helper to extract a key's value from an omnisearch string.
 */
export function extractOmnisearchValue(omnisearch: string | null, key: string): string | undefined {
  if (!omnisearch) return undefined;
  const regex = new RegExp(`(?:^|\\s)${key}:(?:"([^"]+)"|([^\\s]+))`, "i");
  const match = omnisearch.match(regex);
  return match ? (match[1] ?? match[2]) : undefined;
}

/**
 * Parse omnisearch tokens, respecting quotes.
 */
function parseOmnisearchTokens(search: string): string[] {
  const tokens: string[] = [];
  let current = "";
  let inQuotes = false;
  let quoteChar = "";

  for (let i = 0; i < search.length; i++) {
    const char = search[i];

    if (!inQuotes && (char === '"' || char === "'")) {
      inQuotes = true;
      quoteChar = char;
      current += char;
    } else if (inQuotes && char === quoteChar) {
      inQuotes = false;
      quoteChar = "";
      current += char;
    } else if (!inQuotes && char === " ") {
      if (current.trim()) {
        tokens.push(current.trim());
        current = "";
      }
    } else {
      current += char;
    }
  }

  if (current.trim()) {
    tokens.push(current.trim());
  }

  return tokens;
}

/**
 * Break omnisearch string into key-value pairs and non-key-value tokens.
 */
function breakApartOmnisearch(search: string) {
  const tokens = parseOmnisearchTokens(search);
  const nonKvpValues: string[] = [];
  const kvpValues: [string, string][] = [];

  for (const token of tokens) {
    const colonIndex = token.indexOf(":");
    if (colonIndex > 0) {
      const key = token.substring(0, colonIndex);
      const value = token.substring(colonIndex + 1);
      kvpValues.push([key, value]);
    } else {
      nonKvpValues.push(token);
    }
  }

  return {
    nonKvpValues: nonKvpValues.join(" "),
    kvpValues
  };
}

/**
 * Add or update a key in the omnisearch string.
 */
function addOrUpdateKeyInOmnisearch(search: string, key: string, value: string): string {
  const { nonKvpValues, kvpValues } = breakApartOmnisearch(search);
  const filteredKvp = kvpValues.filter(([k]) => k !== key);
  const baseOmnisearch = `${nonKvpValues} ${filteredKvp
    .map(([k, v]) => `${k}:${v}`)
    .join(" ")}`.trim();
  const newKeyValue = `${key}:${value}`;
  return baseOmnisearch ? `${baseOmnisearch} ${newKeyValue}` : newKeyValue;
}

/**
 * Remove a key from the omnisearch string.
 */
function removeKeyInOmnisearch(search: string, key: string): string {
  const { nonKvpValues, kvpValues } = breakApartOmnisearch(search);
  const filteredKvp = kvpValues.filter(([k]) => k !== key);
  return `${nonKvpValues} ${filteredKvp
    .map(([k, v]) => `${k}:${v}`)
    .join(" ")}`.trim();
}

/**
 * Parse omnisearch string into filter keys.
 * This extracts only the filter-relevant parts (not pagination/sorting).
 * Exported so it can be used with useLocation() for React Router integration.
 */
export function parseFilterKeys(omnisearch: string): FilterKeys {
  const filterKeys: FilterKeys = {
    dueDateStart: null,
    dueDateEnd: null,
    states: null,
    statuses: null,
    generatorStatuses: null,
    facilityTagNames: null,
    readyForActionEmail: null,
  };

  if (!omnisearch) return filterKeys;

  // Parse due date
  // IMPORTANT: Force UTC zone when parsing date-only strings to match how the datagrid
  // resolver parses dates. Without this, Luxon interprets date-only strings in local
  // timezone (e.g., "2026-01-01" becomes midnight local time), while the datagrid's
  // new Date("2026-01-01") interprets it as midnight UTC. This timezone mismatch
  // causes events at exactly midnight UTC to be excluded from the chart but included
  // in the datagrid, resulting in count mismatches.
  const dueDateVal = extractOmnisearchValue(omnisearch, "dueDate");
  if (dueDateVal) {
    const [startStr, endStr] = dueDateVal.split("..");
    if (startStr) {
      const start = DateTime.fromISO(startStr, { zone: "utc" });
      if (start.isValid) filterKeys.dueDateStart = start.startOf("day").toISO();
    }
    if (endStr) {
      const end = DateTime.fromISO(endStr, { zone: "utc" });
      if (end.isValid) filterKeys.dueDateEnd = end.endOf("day").toISO();
    }
  }

  // Parse states
  const statesVal = extractOmnisearchValue(omnisearch, "states");
  if (statesVal && statesVal !== "none") {
    const states = statesVal.split(/[|,]/).filter((s) => s.length > 0 && US_STATES.includes(s));
    // Only set if subset of all states (all = no filter)
    if (states.length > 0 && states.length < US_STATES.length) {
      filterKeys.states = states;
    }
  }

  // Parse statuses
  const statusVal = extractOmnisearchValue(omnisearch, "submissionStatus");
  if (statusVal && statusVal !== "none") {
    const statuses = statusVal.split(/[|,]/).filter((s) => s.length > 0 && ALL_STATUSES.includes(s as SubmissionStatus));
    // Only set if subset of all statuses
    if (statuses.length > 0 && statuses.length < ALL_STATUSES.length) {
      filterKeys.statuses = statuses as SubmissionStatus[];
    }
  }

  // Parse generator statuses
  const genStatusVal = extractOmnisearchValue(omnisearch, "generatorStatus");
  if (genStatusVal) {
    const genStatuses = genStatusVal.split(/[|,]/).filter((s) => s.length > 0 && ALL_GENERATOR_STATUSES.includes(s as RegulatoryStatusOption));
    if (genStatuses.length > 0 && genStatuses.length < ALL_GENERATOR_STATUSES.length) {
      filterKeys.generatorStatuses = genStatuses as RegulatoryStatusOption[];
    }
  }

  // Parse facility tags
  const tagsVal = extractOmnisearchValue(omnisearch, "facilityTags");
  if (tagsVal) {
    const tags = tagsVal.split(/[|,]/).filter((t) => t.length > 0);
    if (tags.length > 0) {
      filterKeys.facilityTagNames = tags;
    }
  }

  // Parse readyForAction
  const readyForActionVal = extractOmnisearchValue(omnisearch, "readyForAction");
  if (readyForActionVal) {
    filterKeys.readyForActionEmail = readyForActionVal;
  }

  return filterKeys;
}

/**
 * Deep equality check for FilterKeys to determine if chart queries need to refetch.
 * Exported for use with useLocation()-based hooks that need referential stability.
 */
export function filterKeysEqual(a: FilterKeys, b: FilterKeys): boolean {
  return (
    a.dueDateStart === b.dueDateStart &&
    a.dueDateEnd === b.dueDateEnd &&
    a.readyForActionEmail === b.readyForActionEmail &&
    arraysEqual(a.states, b.states) &&
    arraysEqual(a.statuses, b.statuses) &&
    arraysEqual(a.generatorStatuses, b.generatorStatuses) &&
    arraysEqual(a.facilityTagNames, b.facilityTagNames)
  );
}

function arraysEqual<T>(a: T[] | null, b: T[] | null): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
}

// ============================================================================
// URL-based store using useSyncExternalStore
// ============================================================================

// Cache for referential stability
let cachedState: OmnisearchState = {
  omnisearch: "",
  filterKeys: INITIAL_FILTER_KEYS,
};

// Track the last omnisearch value we notified about to prevent duplicate notifications
let lastNotifiedOmnisearch: string | null = null;

// Subscribers for URL changes
const subscribers = new Set<() => void>();

// Flag to track if we've installed the URL interceptors
let interceptorsInstalled = false;
let originalPushState: typeof history.pushState | null = null;
let originalReplaceState: typeof history.replaceState | null = null;

/**
 * Notify all subscribers that URL has changed.
 * Deduplicates notifications - only notifies if omnisearch actually changed.
 */
function notifySubscribers() {
  // Check if omnisearch actually changed to prevent duplicate notifications
  // This can happen when both history.replaceState() and React Router's navigate()
  // are called for the same URL change.
  const params = new URLSearchParams(window.location.search);
  const currentOmnisearch = params.get("omnisearch") ?? "";

  if (currentOmnisearch === lastNotifiedOmnisearch) {
    return; // URL hasn't changed, skip notification
  }

  lastNotifiedOmnisearch = currentOmnisearch;
  subscribers.forEach((callback) => callback());
}

/**
 * Install URL change interceptors (pushState, replaceState, popstate).
 * Only installed once, shared across all subscribers.
 *
 * IMPORTANT: This must be called BEFORE React Router initializes (before createBrowserRouter).
 * React Router caches references to history.pushState/replaceState at initialization time.
 * If we install interceptors after React Router, navigate() will use the original methods
 * and bypass our interceptors, causing charts to not update when typing in omnisearch.
 *
 * Call this from main.tsx before createBrowserRouter().
 */
export function installHistoryInterceptors() {
  if (interceptorsInstalled) return;
  interceptorsInstalled = true;

  // Store original methods
  originalPushState = history.pushState.bind(history);
  originalReplaceState = history.replaceState.bind(history);

  // Intercept pushState
  history.pushState = (...args) => {
    originalPushState?.(...args);
    notifySubscribers();
  };

  // Intercept replaceState
  history.replaceState = (...args) => {
    originalReplaceState?.(...args);
    notifySubscribers();
  };

  // Listen to popstate (back/forward navigation)
  window.addEventListener("popstate", notifySubscribers);
}

/**
 * Subscribe to URL changes.
 * Called by useSyncExternalStore.
 */
function subscribeToURL(callback: () => void): () => void {
  // Ensure interceptors are installed (should already be done in main.tsx, but this is a safety net)
  installHistoryInterceptors();

  // Add subscriber
  subscribers.add(callback);

  // Return cleanup function
  return () => {
    subscribers.delete(callback);
  };
}

// Counter for getSnapshot calls - for debugging
let getSnapshotCallCount = 0;

/**
 * Get current snapshot by reading URL.
 * Called synchronously during render by useSyncExternalStore.
 * Returns cached state if URL hasn't changed (referential stability).
 */
function getSnapshot(): OmnisearchState {
  getSnapshotCallCount++;
  const params = new URLSearchParams(window.location.search);
  const omnisearch = params.get("omnisearch") ?? "";

  // Return cached state if omnisearch hasn't changed
  if (omnisearch === cachedState.omnisearch) {
    return cachedState;
  }

  // Parse new filter keys
  const newFilterKeys = parseFilterKeys(omnisearch);

  // Maintain filterKeys reference stability if content is equal
  const filterKeysAreEqual = filterKeysEqual(cachedState.filterKeys, newFilterKeys);
  const stableFilterKeys = filterKeysAreEqual
    ? cachedState.filterKeys
    : newFilterKeys;

  // INSTRUMENTATION: Log when omnisearch changes
  metricsLogger.emitWideEvent({
    event_type: "URL_READ",
    component_name: "omnisearchStore.getSnapshot",
    reason_keys: ["omnisearch"],
    vars_json: {
      callCount: getSnapshotCallCount,
      prevOmnisearch: cachedState.omnisearch,
      newOmnisearch: omnisearch,
      filterKeysAreEqual,
      filterKeysRefChanged: !filterKeysAreEqual,
      prevFilterKeys: cachedState.filterKeys,
      newFilterKeys: stableFilterKeys,
    },
  });

  // Update cache
  cachedState = {
    omnisearch,
    filterKeys: stableFilterKeys,
  };

  return cachedState;
}

/**
 * Server snapshot - empty state for SSR.
 */
function getServerSnapshot(): OmnisearchState {
  return {
    omnisearch: "",
    filterKeys: INITIAL_FILTER_KEYS,
  };
}

// ============================================================================
// Action creators - write to URL
// ============================================================================

/**
 * Update URL with new search params.
 * Uses replaceState to avoid polluting history with every change.
 * Wrapped in startTransition so Suspense boundaries show stale data
 * instead of falling back to skeleton during filter changes.
 */
function updateURL(newOmnisearch: string, useReplace = true) {
  startTransition(() => {
    const params = new URLSearchParams(window.location.search);

    if (newOmnisearch) {
      params.set("omnisearch", newOmnisearch);
    } else {
      params.delete("omnisearch");
    }

    const newSearch = params.toString();
    const newUrl = newSearch
      ? `${window.location.pathname}?${newSearch}`
      : window.location.pathname;

    if (useReplace) {
      history.replaceState(null, "", newUrl);
    } else {
      history.pushState(null, "", newUrl);
    }
  });
}

/**
 * Set the omnisearch value, replacing the current URL.
 */
function setOmnisearch(value: string) {
  updateURL(value, true);
}

/**
 * Set the omnisearch value immediately (same as setOmnisearch, kept for API compatibility).
 */
function setOmnisearchImmediate(value: string) {
  updateURL(value, true);
}

/**
 * Edit multiple key-value pairs in the omnisearch.
 */
function editKeyValues(keyValues: [string, string][]) {
  const current = getSnapshot().omnisearch;
  let updated = current;
  for (const [key, value] of keyValues) {
    updated = addOrUpdateKeyInOmnisearch(updated, key, value);
  }
  updateURL(updated, true);
}

/**
 * Remove a key from the omnisearch.
 */
function removeKey(key: string) {
  const current = getSnapshot().omnisearch;
  const updated = removeKeyInOmnisearch(current, key);
  updateURL(updated, true);
}

// ============================================================================
// Selector Hooks - Components subscribe to ONLY the state slices they need
// ============================================================================

/**
 * Hook to get the full omnisearch state using useSyncExternalStore.
 * This reads URL synchronously during render, avoiding effect timing issues.
 */
export function useOmnisearchStore(): OmnisearchState {
  return useSyncExternalStore(subscribeToURL, getSnapshot, getServerSnapshot);
}

/**
 * For chart components - only re-renders when filter-relevant state changes.
 * Does NOT re-render when pagination/sorting changes.
 */
export function useChartFilterKeys(): FilterKeys {
  const state = useSyncExternalStore(subscribeToURL, getSnapshot, getServerSnapshot);
  return state.filterKeys;
}

/**
 * For DataGrid - subscribes to full omnisearch string.
 */
export function useFullOmnisearch(): string {
  const state = useSyncExternalStore(subscribeToURL, getSnapshot, getServerSnapshot);
  return state.omnisearch;
}

/**
 * For components that need omnisearch actions.
 * Actions are stable - they write directly to URL.
 */
export function useOmnisearchActions() {
  // Memoize action references to ensure stability
  return useMemo(() => ({
    setOmnisearch,
    setOmnisearchImmediate,
    editKeyValues,
    removeKey,
  }), []);
}

// ============================================================================
// Helper to convert FilterKeys to GraphQL filter format
// ============================================================================

/**
 * Convert FilterKeys to the GraphQL WasteSubmissionsSummaryFilter format.
 */
export function buildFilterFromKeys(filterKeys: FilterKeys): WasteSubmissionsSummaryFilter | undefined {
  const filter: WasteSubmissionsSummaryFilter = {};

  if (filterKeys.dueDateStart) {
    filter.dueDateStart = filterKeys.dueDateStart;
  }
  if (filterKeys.dueDateEnd) {
    filter.dueDateEnd = filterKeys.dueDateEnd;
  }
  if (filterKeys.states) {
    filter.states = filterKeys.states;
  }
  if (filterKeys.statuses) {
    filter.statuses = filterKeys.statuses;
  }
  if (filterKeys.generatorStatuses) {
    filter.generatorStatuses = filterKeys.generatorStatuses;
  }
  if (filterKeys.facilityTagNames) {
    filter.facilityTagNames = filterKeys.facilityTagNames;
  }
  if (filterKeys.readyForActionEmail) {
    filter.readyForActionEmail = filterKeys.readyForActionEmail;
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

// ============================================================================
// Exports for testing
// ============================================================================

/**
 * Reset the cache - useful for testing.
 */
export function __resetCache() {
  cachedState = {
    omnisearch: "",
    filterKeys: INITIAL_FILTER_KEYS,
  };
  lastNotifiedOmnisearch = null;
}

/**
 * Exports for testing internal functions.
 */
export const __testing = {
  parseFilterKeys,
  filterKeysEqual,
  arraysEqual,
  parseOmnisearchTokens,
  breakApartOmnisearch,
  addOrUpdateKeyInOmnisearch,
  removeKeyInOmnisearch,
};
